/**
 * Fase 2D · GestorContextBanner
 * Cinta visible siempre bajo el topbar cuando el usuario es GESTOR.
 * Muestra: "Estás gestionando: [Centro]" para eliminar ambigüedad sobre
 * qué organización se está modificando.
 * NO destructivo: si solo hay 1 org, muestra el nombre sin dropdown.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Buildings, MapPin, Sparkle, ArrowRight } from "@phosphor-icons/react";

export default function GestorContextBanner() {
  const [orgs, setOrgs] = useState(null);
  const [activeId, setActiveId] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await api.get("/me/orgs");
        if (!mounted) return;
        setOrgs(data.items || []);
        setActiveId(data.active_org_id || null);
      } catch {
        if (mounted) setOrgs([]);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const active = useMemo(
    () => (orgs || []).find(o => o.id === activeId) || (orgs || [])[0],
    [orgs, activeId]
  );

  if (!orgs) return null;

  // Empty: gestor sin centros
  if (orgs.length === 0) {
    return (
      <div
        className="mb-6 flex items-center justify-between gap-3 px-4 py-3 rounded border border-amber-500/30 bg-amber-500/10 flex-wrap"
        data-testid="gestor-context-banner-empty"
      >
        <div className="flex items-start gap-3">
          <Sparkle size={16} weight="fill" className="text-amber-400 shrink-0 mt-0.5" />
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-amber-200">Todavía no gestionas ningún centro</div>
            <div className="text-[11px] text-amber-200/80 mt-0.5">Comparte tu código de gestor con tus clientes titulares.</div>
          </div>
        </div>
        <Link
          to="/gestor/onboarding"
          className="text-[11px] uppercase tracking-wider font-bold text-amber-100 hover:text-white inline-flex items-center gap-1"
          data-testid="gestor-context-banner-code-cta"
        >
          Ver mi código <ArrowRight size={11} weight="bold" />
        </Link>
      </div>
    );
  }

  if (!active) return null;

  return (
    <div
      className="mb-6 flex items-center justify-between gap-3 px-4 py-2.5 rounded border border-[var(--ag-gold)]/30 bg-gradient-to-r from-[var(--ag-gold)]/10 via-transparent to-transparent flex-wrap"
      data-testid="gestor-context-banner"
    >
      <div className="flex items-center gap-3 flex-1 min-w-0">
        {active.logo_url ? (
          <img src={active.logo_url} alt="" className="w-8 h-8 rounded-sm object-cover shrink-0" />
        ) : (
          <div className="w-8 h-8 rounded-sm bg-[var(--ag-midnight)] border border-[var(--ag-border)] grid place-items-center shrink-0">
            <Buildings size={14} weight="duotone" className="text-[var(--ag-gold)]" />
          </div>
        )}
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--ag-text-dim)] font-semibold">
            Estás gestionando
          </div>
          <div className="font-display font-bold text-sm text-[var(--ag-gold)] truncate" data-testid="gestor-context-banner-org-name">
            {active.name}
          </div>
        </div>
        {active.city && (
          <div className="text-[11px] text-[var(--ag-text-dim)] inline-flex items-center gap-1 shrink-0 hidden sm:inline-flex">
            <MapPin size={10} /> {active.city}
          </div>
        )}
      </div>
      {orgs.length > 1 && (
        <span className="text-[10px] uppercase tracking-wider text-[var(--ag-text-dim)] shrink-0" data-testid="gestor-context-banner-count">
          {orgs.length} centros
        </span>
      )}
    </div>
  );
}
